import {zodResolver} from "@hookform/resolvers/zod";
import {Loader2} from "lucide-react";
import {useMemo} from "react";
import {useForm} from "react-hook-form";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {useTranslation} from "react-i18next";

import {Button} from "@/components/ui/button";
import {Form, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form";
import {Input} from "@/components/ui/input";
import {PublicPageShell} from "@/src/modules/core/components";
import {AppVersionInfo} from "@/src/modules/core/components/app-version-info";
import {LOGO_URL_DESKTOP} from "@/src/modules/core/utils/constants";
import {peekRequiresProfileCompletionPending} from "@/src/modules/resident-claim/lib/claim-profile-pending";

import {PasswordInput, SessionEndedBanner} from "../components";
import {useLogin} from "../hooks";
import {clearSessionEndedLoginSearch} from "../lib/session-ended-copy";
import {createLoginSchema, type LoginFormValues} from "../schemas";
import {authSerializer} from "../services/serializer";

export default function LoginPage() {
  const {t} = useTranslation("auth");
  const {mutate: login, isPending} = useLogin();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const afterClaimVerify = peekRequiresProfileCompletionPending();
  const loginSchema = useMemo(() => createLoginSchema(t), [t]);

  const form = useForm<LoginFormValues>({
    defaultValues: {
      user_name: "",
      password: "",
      remember_me: true,
    },
    resolver: zodResolver(loginSchema),
  });

  const handleFormSubmit = (data: LoginFormValues) => {
    clearSessionEndedLoginSearch(navigate, searchParams);
    const loginData = authSerializer.serializeLogin(data);

    login(loginData);
  };

  return (
    <PublicPageShell>
      <div className="rounded-lg bg-background p-6 shadow-lg sm:p-8">
        <div className="mb-8 flex flex-col items-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full">
            <img src={LOGO_URL_DESKTOP} alt="Image" height="100" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">{t("brand.name")}</h1>
          <p className="text-muted-foreground">{t("brand.tagline")}</p>
        </div>

        <h2 className="mb-6 text-center text-xl font-semibold text-foreground">
          {t("login.title")}
        </h2>

        <SessionEndedBanner />

        {afterClaimVerify ? (
          <p className="mb-4 text-center text-sm text-muted-foreground">
            Sign in with the email and password you chose when claiming your account. You will
            complete your profile next.
          </p>
        ) : null}

        <Form {...form}>
          <form
            id="login-form"
            className="flex flex-col gap-4"
            onSubmit={form.handleSubmit(handleFormSubmit)}
          >
            <FormField
              control={form.control}
              name="user_name"
              render={({field, formState}) => (
                <FormItem>
                  <FormLabel>
                    {afterClaimVerify ? t("forgot.email") : t("login.username")}
                  </FormLabel>
                  <Input
                    id="user_name"
                    type={afterClaimVerify ? "email" : "text"}
                    placeholder={
                      afterClaimVerify
                        ? t("forgot.email_placeholder")
                        : t("login.username_placeholder")
                    }
                    className="border-border"
                    disabled={isPending}
                    {...field}
                  />
                  {formState.errors.user_name ? (
                    <FormMessage>{formState.errors.user_name.message}</FormMessage>
                  ) : null}
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({field, formState}) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>{t("login.password")}</FormLabel>
                    <Link
                      to="/forgot"
                      className="text-sm font-medium text-primary hover:text-primary/80"
                    >
                      {t("login.forgot_password")}
                    </Link>
                  </div>
                  <PasswordInput {...field} />
                  {formState.errors.password ? (
                    <FormMessage>{formState.errors.password.message}</FormMessage>
                  ) : null}
                </FormItem>
              )}
            />

            <Button type="submit" className="w-full" disabled={isPending}>
              {isPending ? (
                <>
                  {t("login.submitting")}
                  <Loader2 className="mr-2 size-4 animate-spin" />
                </>
              ) : (
                t("login.submit")
              )}
            </Button>
          </form>
        </Form>
      </div>

      <div className="mt-6 flex flex-col items-center gap-2">
        <AppVersionInfo />
        <p className="text-center text-xs text-muted-foreground">
          {t("brand.copyright", {year: new Date().getFullYear()})}
        </p>
      </div>
    </PublicPageShell>
  );
}
